import { useEffect, useRef, useContext, useState } from "react";
import { sanitizeHtml } from '@/lib/sanitize-html';
import { cn, linkifyText, linkifyHtml, renderMessageContent, isTodayInBerlin, MentionUserMap } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { format, isToday } from "date-fns";
import { Bot, AlertCircle, FileText, FileArchive, FileSpreadsheet, File as FileIcon, FileImage, Download, ExternalLink, Reply, X, PhoneMissed, Phone, Video } from "lucide-react";
import { useCall } from "@/contexts/call-context";
import { ReactionBar, ReactionBubbles } from "./Reactions";
import { AuthenticatedImage } from "./AuthenticatedImage";
import { useExpansion } from "@/contexts/expansion-context";
import { DialogContext } from "@/contexts/dialog-context";
import { darkenColor } from "@/lib/color-utils";
import { StatusBubble } from "@/components/status-bubble";
import { getUserColor, DEFAULT_USER_COLOR } from '@/lib/user-color';
import { nativeAuthedDownload } from './native-download';

function getFileExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toUpperCase() || '';
  return ext.length > 5 ? ext.slice(0, 5) : ext;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType: string, extension: string) {
  if (mimeType.startsWith('image/')) return FileImage;
  if (mimeType === 'application/pdf' || extension === 'PDF') return FileText;
  if (mimeType.includes('zip') || mimeType.includes('archive') || ['ZIP', 'RAR', '7Z', 'TAR', 'GZ'].includes(extension)) return FileArchive;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || ['XLS', 'XLSX', 'CSV'].includes(extension)) return FileSpreadsheet;
  if (mimeType.includes('document') || mimeType.includes('word') || ['DOC', 'DOCX', 'TXT', 'RTF'].includes(extension)) return FileText;
  return FileIcon;
}

function ensureUTCDate(val: string | Date): Date {
  if (val instanceof Date) return val;
  const str = String(val);
  if (!str.includes('Z') && !str.includes('+') && !/\d{2}:\d{2}$/.test(str)) {
    return new Date(str.replace(' ', 'T') + 'Z');
  }
  return new Date(str);
}

function formatMessageTime(timestamp: Date): string {
  if (isToday(timestamp)) {
    return format(timestamp, 'HH:mm');
  }
  return format(timestamp, 'dd.MM. HH:mm');
}

interface MessageAuthor {
  id: number;
  username: string;
  firstName?: string | null;
  lastName?: string | null;
  colorCode?: string | null;
}

interface ReactionEntry {
  id: string;
  targetType: string;
  targetId: string;
  reactorId: number;
  type: 'emoji' | 'text';
  content: string;
  createdAt: Date | string;
  reactor?: {
    id: number;
    username?: string;
    firstName?: string | null;
    lastName?: string | null;
    shortCode?: string | null;
    colorCode?: string | null;
  };
}

interface ChatAttachment {
  id: string;
  originalFilename: string;
  contentType: string;
  sizeBytes?: number;
  metadata?: {
    isImage?: boolean;
  };
}

interface PulseShareMetadata {
  pulseId: number;
  projectId: string;
  projectName: string;
  pulseDescription: string;
  sequenceNumber: string;
  status: string;
  directShare?: {
    url: string;
    type: 'preview' | 'final';
    clientExpired: boolean;
  };
}

function safeDirectShareUrl(value: unknown): string | null {
  if (typeof value !== 'string' || !value || /[\s\\\u0000-\u001f\u007f]/.test(value)) return null;
  // Root-relative paths stay on this origin; never allow protocol-relative URLs.
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  if (!/^https?:\/\//i.test(value)) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? value : null;
  } catch {
    return null;
  }
}

interface MissedCallMetadata {
  callerId: number;
  calleeId: number;
  modality: 'audio' | 'video';
  sessionId: string;
}

interface MessageMetadata {
  type?: 'text' | 'audio' | 'file' | 'system' | 'pulse_share' | 'missed_call';
  pulseShare?: PulseShareMetadata;
  missedCall?: MissedCallMetadata;
  optionalMessage?: string;
  urgentTagRecipient?: { userId: number; username: string; name: string };
}

interface ChatMessage {
  id: string;
  content: string;
  richContent?: any;
  authorId: number | null;
  integrationId?: string | null;
  integrationName?: string | null;
  integrationAvatar?: string | null;
  externalAuthorKind?: string | null;
  externalAuthorName?: string | null;
  isSystemMessage?: boolean;
  createdAt: Date | string;
  editedAt?: Date | null;
  author?: MessageAuthor | null;
  reactionEntries?: ReactionEntry[];
  attachments?: ChatAttachment[];
  metadata?: MessageMetadata;
  replyToId?: string | null; // Parent message ID for threaded replies
}

interface PreviewAttachment {
  id: string;
  filename: string;
  contentType: string;
}

interface ChatMessageBubbleProps {
  message: ChatMessage;
  isOwn: boolean;
  showAuthor?: boolean;
  currentUserId?: number;
  channelId?: string;
  channelType?: string;
  compact?: boolean;
  highlightMessageId?: string | null;
  onHighlightComplete?: () => void;
  mentionUserMap?: MentionUserMap;
  onPreviewAttachment?: (attachment: PreviewAttachment) => void;
  isReply?: boolean;
  onReply?: (messageId: string, authorName: string) => void;
  onDelete?: (messageId: string, messageContent: string) => void;
  isAdmin?: boolean;
}

function getAuthorInfo(message: ChatMessage): { name: string; avatar?: string; color?: string; isBot: boolean } {
  if (message.integrationId) {
    return {
      name: message.integrationName || 'Bot',
      avatar: message.integrationAvatar || undefined,
      color: '#6366f1',
      isBot: true,
    };
  }
  
  // External client authors (public share page, task #74) — yellow identity
  if (message.externalAuthorKind === 'client') {
    return {
      name: message.externalAuthorName || 'Kunde',
      color: '#eab308',
      isBot: false,
    };
  }

  if (message.author) {
    const name = message.author.firstName && message.author.lastName
      ? `${message.author.firstName} ${message.author.lastName}`
      : message.author.username;
    return {
      name,
      color: getUserColor(message.author.colorCode),
      isBot: false,
    };
  }
  
  return {
    name: 'Unknown',
    color: DEFAULT_USER_COLOR,
    isBot: false,
  };
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function handleDownload(attachmentId: string, filename: string) {
  // Auth rides on cookies; the server sets Content-Disposition via
  // ?download=true. See native-download.ts for the rationale.
  void nativeAuthedDownload(`/api/chat/attachments/${attachmentId}?download=true`, filename);
}

export function ChatMessageBubble({ 
  message, 
  isOwn, 
  showAuthor = true, 
  currentUserId, 
  channelId, 
  channelType,
  compact = false,
  highlightMessageId,
  onHighlightComplete,
  mentionUserMap,
  onPreviewAttachment,
  isReply = false,
  onReply,
  onDelete,
  isAdmin = false,
}: ChatMessageBubbleProps) {
  const messageRef = useRef<HTMLDivElement>(null);
  const isHighlighted = highlightMessageId === message.id;
  const showStatus = channelType === 'direct' || channelType === 'project';
  
  // Expansion context for pulse navigation (same pattern as notification-bell)
  const { expandMultiple, expandedItems } = useExpansion();
  const dialogContext = useContext(DialogContext);
  const [pendingPulseNav, setPendingPulseNav] = useState<{
    pulseId: number;
    requiredExpansions: string[];
  } | null>(null);

  useEffect(() => {
    if (isHighlighted && messageRef.current) {
      messageRef.current.scrollIntoView({ block: 'center', behavior: 'instant' });
      const timer = setTimeout(() => {
        onHighlightComplete?.();
      }, 3100);
      return () => clearTimeout(timer);
    }
  }, [isHighlighted, onHighlightComplete]);
  
  // Watch for expansion completion and open pulse dialog when ready (same as notification-bell)
  useEffect(() => {
    if (!pendingPulseNav || !dialogContext) return;

    const allExpanded = pendingPulseNav.requiredExpansions.every(
      id => expandedItems[id]
    );

    if (allExpanded) {
      dialogContext.setDialogOpen(`pulse-detail-${pendingPulseNav.pulseId}`, true);
      setPendingPulseNav(null);
    }
  }, [expandedItems, pendingPulseNav, dialogContext]);
  
  // Handle click on pulse share card - navigate to pulse
  const handlePulseCardClick = (pulseShare: PulseShareMetadata) => {
    const shortYear = pulseShare.projectId.split('-')[0];
    const requiredExpansions = [
      `year-${shortYear}`,
      `project-${pulseShare.projectId}`,
      `pulse-${pulseShare.pulseId}`
    ];
    
    expandMultiple(requiredExpansions);
    setPendingPulseNav({
      pulseId: pulseShare.pulseId,
      requiredExpansions,
    });
  };

  if (message.isSystemMessage) {
    return (
      <div 
        className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground"
        data-testid={`message-system-${message.id}`}
      >
        <AlertCircle className="w-3 h-3" />
        <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMessageContent(message.content, mentionUserMap)) }} />
      </div>
    );
  }
  
  // Render missed-call entry with Call Back button
  if (message.metadata?.type === 'missed_call' && message.metadata.missedCall) {
    return (
      <MissedCallBubble
        message={message}
        missedCall={message.metadata.missedCall}
        currentUserId={currentUserId}
        channelId={channelId}
        isHighlighted={isHighlighted}
        messageRef={messageRef}
      />
    );
  }

  // Render pulse share card
  if (message.metadata?.type === 'pulse_share' && message.metadata.pulseShare) {
    const pulseShare = message.metadata.pulseShare;
    const directShare = pulseShare.directShare;
    const directShareUrl = directShare?.type === 'preview' || directShare?.type === 'final'
      ? safeDirectShareUrl(directShare.url)
      : null;
    const directShareLabel = directShare?.type === 'final' ? 'Open Final' : 'Open Preview';
    const authorInfo = getAuthorInfo(message);
    const timestamp = ensureUTCDate(message.createdAt);
    // Older urgent tags already stored the recipient in their text, which this
    // card used to hide. Keep those messages legible without rewriting history.
    const legacyRecipient = /^@([^\r\n]+) — Urgent Tag\r?$/.exec(message.content.split('\n', 1)[0])?.[1];
    const recipient = message.metadata.urgentTagRecipient;
    const urgentRecipientName = recipient
      ? recipient.name || mentionUserMap?.get(recipient.username) || recipient.username
      : legacyRecipient ? mentionUserMap?.get(legacyRecipient) || legacyRecipient : null;
    
    return (
      <div 
        ref={messageRef}
        className={cn(
          "flex gap-2 max-w-[85%]",
          isOwn ? "ml-auto flex-row-reverse" : "mr-auto",
          isHighlighted && "animate-fade-highlight rounded-lg"
        )}
        data-testid={`message-pulse-share-${message.id}`}
        id={`message-${message.id}`}
      >
        {showAuthor && (
          <div className="relative flex-shrink-0">
            <Avatar className="w-8 h-8">
              {authorInfo.avatar ? (
                <AvatarImage src={authorInfo.avatar} alt={authorInfo.name} />
              ) : null}
              <AvatarFallback 
                style={{ backgroundColor: getUserColor(authorInfo.color) }}
                className="text-white text-xs"
              >
                {authorInfo.isBot ? <Bot className="w-4 h-4" /> : getInitials(authorInfo.name)}
              </AvatarFallback>
            </Avatar>
            {showStatus && message.author?.id && !authorInfo.isBot && (
              <StatusBubble userId={message.author.id} size="sm" className="absolute -bottom-0.5 -right-0.5" />
            )}
          </div>
        )}
        
        <div className={cn("flex flex-col gap-1 min-w-0", isOwn && "items-end")}>
          {showAuthor && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-foreground">
                {authorInfo.name}
              </span>
              <span className="text-[10px] text-slate-400">
                {formatMessageTime(timestamp)}
              </span>
            </div>
          )}
          
          {urgentRecipientName && (
            <div className="text-xs font-semibold text-amber-300 break-words"
              data-testid="urgent-tag-recipient">
              Urgent tag for <span>@{urgentRecipientName}</span>
            </div>
          )}

          {/* Optional message text */}
          {message.metadata.optionalMessage && (
            <div className="text-sm text-foreground mb-1 whitespace-pre-wrap break-words"
              dangerouslySetInnerHTML={{ __html: linkifyText(
                message.metadata.optionalMessage
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;')
              ) }}
            />
          )}
          
          {/* Pulse Share Card */}
          <div 
            className={cn(
              "border border-white/10 bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors group",
              !directShareUrl && "cursor-pointer"
            )}
            onClick={directShareUrl ? undefined : () => handlePulseCardClick(pulseShare)}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-4 bg-primary rounded-full" />
              <span className="font-bold text-sm text-white">
                {pulseShare.projectId}: {pulseShare.projectName}
              </span>
            </div>
            <div className="text-xs text-muted-foreground line-clamp-2">
              {pulseShare.sequenceNumber && (
                <span className="text-primary mr-1 font-medium">{pulseShare.sequenceNumber}</span>
              )}
              {pulseShare.pulseDescription}
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[10px] text-muted-foreground capitalize">
                Status: {pulseShare.status}
              </span>
              {directShareUrl ? (
                <a
                  href={directShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-[10px] text-primary uppercase tracking-wider font-semibold hover:underline"
                >
                  {directShareLabel}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : (
                <div className="flex items-center gap-1 text-[10px] text-primary uppercase tracking-wider font-semibold group-hover:underline">
                  Open Pulse
                  <ExternalLink className="w-3 h-3" />
                </div>
              )}
            </div>
            {directShareUrl && (
              <div className="mt-2 space-y-2">
                <a
                  href={directShareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-xs text-primary underline break-all select-text"
                >
                  {directShareUrl}
                </a>
                {directShare?.clientExpired && (
                  <div className="flex items-start gap-1 text-xs text-amber-400" role="note">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>Client access has expired. Team members can still open this link.</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const authorInfo = getAuthorInfo(message);
  const timestamp = ensureUTCDate(message.createdAt);
  const isFromToday = isTodayInBerlin(timestamp);
  
  const reactions = message.reactionEntries || [];
  const hasReactions = reactions.length > 0;
  const attachments = message.attachments || [];

  if (compact) {
    return (
      <div 
        ref={messageRef}
        className={cn(
          "flex transition-colors duration-500",
          isOwn ? "justify-end" : "justify-start",
          isHighlighted && "animate-fade-highlight rounded-lg"
        )}
        data-testid={`message-${message.id}`}
        data-message-id={message.id}
        id={`message-${message.id}`}
      >
        <div
          className={cn(
            "max-w-[80%] rounded-xl px-3 py-1.5 relative group/bubble",
            isOwn
              ? "text-primary-foreground"
              : message.author?.colorCode
                ? "text-white"
                : "text-foreground",
            hasReactions && "mb-4",
            isFromToday && "bubble-today",
            isFromToday && isOwn && "bubble-today-own"
          )}
          style={{
            backgroundColor: isOwn
              ? 'hsl(221.2 83.2% 26.6%)'
              : message.author?.colorCode
                ? darkenColor(message.author.colorCode, 0.5)
                : 'hsl(215 20.2% 30%)'
          }}
        >
          <div className="flex items-center justify-between gap-2 mb-0.5">
            {!isOwn ? (
              <span className={cn(
                "text-[10px] font-semibold",
                message.author?.colorCode ? "text-white" : "text-foreground"
              )}>
                {authorInfo.name}
              </span>
            ) : (
              <span></span>
            )}
            <span className={cn(
              "text-[9px]",
              isOwn 
                ? "text-primary-foreground/70"
                : message.author?.colorCode 
                  ? "text-white/80" 
                  : "text-slate-400"
            )}>
              {formatMessageTime(timestamp)}
            </span>
          </div>
          {message.content && (
            <p 
              className="text-xs whitespace-pre-wrap break-words"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMessageContent(message.richContent || message.content, mentionUserMap)) }}
            />
          )}
          {attachments.length > 0 && (
            <div className="mt-1 space-y-1">
              {attachments.map((att) => {
                const isImage = att.metadata?.isImage || att.contentType.startsWith('image/');
                const isVideo = att.contentType.startsWith('video/');
                const isPdf = att.contentType === 'application/pdf';
                const isPreviewable = isImage || isVideo || isPdf;
                
                const handleClick = () => {
                  if (isPreviewable && onPreviewAttachment) {
                    onPreviewAttachment({
                      id: att.id,
                      filename: att.originalFilename,
                      contentType: att.contentType,
                    });
                  } else {
                    handleDownload(att.id, att.originalFilename);
                  }
                };
                
                if (isImage) {
                  return (
                    <div 
                      key={att.id} 
                      className="relative group/attachment inline-block cursor-pointer"
                      onClick={handleClick}
                      title={isPreviewable ? `Click to preview` : `Download ${att.originalFilename}`}
                    >
                      <AuthenticatedImage 
                        attachmentId={att.id}
                        alt={att.originalFilename}
                        className="max-w-full max-h-48 rounded-lg object-contain"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(att.id, att.originalFilename);
                        }}
                        className="absolute top-1 right-1 p-1.5 bg-black/60 rounded-md opacity-0 group-hover/attachment:opacity-100 transition-opacity hover:bg-black/80"
                        title="Download"
                      >
                        <Download className="h-3.5 w-3.5 text-white" />
                      </button>
                    </div>
                  );
                }
                const extension = getFileExtension(att.originalFilename);
                const FileIconComponent = getFileIcon(att.contentType, extension);
                return (
                  <div key={att.id} className="relative group/attachment inline-block">
                    <button
                      onClick={handleClick}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded-lg hover:opacity-80 transition-opacity",
                        isOwn ? "bg-white/10" : "bg-black/10"
                      )}
                      title={isPreviewable ? `Click to preview` : `Download ${att.originalFilename}`}
                    >
                      <FileIconComponent className={cn(
                        "h-4 w-4 shrink-0",
                        isOwn ? "text-primary-foreground/80" : "text-primary"
                      )} />
                      <div className="flex flex-col items-start min-w-0">
                        <span className={cn(
                          "text-[10px] font-medium truncate max-w-[120px]",
                          isOwn ? "text-primary-foreground" : "text-foreground"
                        )}>
                          {att.originalFilename}
                        </span>
                        <span className={cn(
                          "text-[9px]",
                          isOwn ? "text-primary-foreground/60" : "text-muted-foreground"
                        )}>
                          {att.sizeBytes ? formatFileSize(att.sizeBytes) : extension}
                        </span>
                      </div>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(att.id, att.originalFilename);
                      }}
                      className="absolute top-1 right-1 p-1 bg-black/60 rounded opacity-0 group-hover/attachment:opacity-100 transition-opacity hover:bg-black/80"
                      title="Download"
                    >
                      <Download className="h-3 w-3 text-white" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          
          {channelId && (
            <div 
              className={cn(
                "absolute -top-2 z-20 opacity-0 group-hover/bubble:opacity-100 transition-opacity flex items-center gap-1",
                isOwn ? "-left-2" : "-right-2"
              )}
            >
              <ReactionBar
                targetType="chat_channel_message"
                targetId={message.id}
                channelId={channelId}
              />
              {onReply && !isReply && (
                <button
                  onClick={() => {
                    const authorInfo = message.author
                      ? message.author.firstName || message.author.username
                      : 'Unknown';
                    onReply(message.id, authorInfo);
                  }}
                  className="h-6 px-1.5 rounded bg-slate-700/90 backdrop-blur-sm hover:bg-slate-600 border border-white/20 text-white/90 hover:text-white transition-colors"
                  title="Reply to this message"
                >
                  <Reply className="h-3.5 w-3.5 text-white" />
                </button>
              )}
              {isAdmin && onDelete && (
                <button
                  onClick={() => onDelete(message.id, message.content || '')}
                  className="h-6 px-1.5 rounded bg-red-700/90 backdrop-blur-sm hover:bg-red-600 border border-white/20 text-white/90 hover:text-white transition-colors"
                  title="Delete this message"
                >
                  <X className="h-3.5 w-3.5 text-white" />
                </button>
              )}
            </div>
          )}
          
          {currentUserId && hasReactions && (
            <div className="absolute -bottom-3 left-2 z-10">
              <ReactionBubbles
                reactions={reactions}
                currentUserId={currentUserId}
                targetType="chat_channel_message"
                targetId={message.id}
                channelId={channelId}
                isOwn={isOwn}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={messageRef}
      className={cn(
        "flex gap-2 max-w-[80%] group",
        isOwn ? "ml-auto flex-row-reverse" : "mr-auto",
        hasReactions && "mb-4",
        isHighlighted && "animate-fade-highlight rounded-lg"
      )}
      data-testid={`message-bubble-${message.id}`}
      id={`message-${message.id}`}
    >
      {showAuthor ? (
        <div className={cn("relative flex-shrink-0", isReply ? "w-6 h-6" : "w-8 h-8")}>
          <Avatar 
            className={cn(isReply ? "w-6 h-6" : "w-8 h-8")} 
            data-testid={`avatar-${message.id}`}
          >
            {authorInfo.avatar ? (
              <AvatarImage src={authorInfo.avatar} alt={authorInfo.name} />
            ) : null}
            <AvatarFallback 
              style={{ backgroundColor: getUserColor(authorInfo.color) }}
              className={cn("text-white", isReply ? "text-[10px]" : "text-xs")}
            >
              {authorInfo.isBot ? <Bot className={isReply ? "w-3 h-3" : "w-4 h-4"} /> : getInitials(authorInfo.name)}
            </AvatarFallback>
          </Avatar>
          {showStatus && message.author?.id && !authorInfo.isBot && !isReply && (
            <StatusBubble userId={message.author.id} size="sm" className="absolute -bottom-0.5 -right-0.5" />
          )}
        </div>
      ) : (
        <div className={cn("flex-shrink-0", isReply ? "w-6" : "w-8")} />
      )}
      
      <div className={cn("flex flex-col gap-1", isOwn && "items-end")}>
        {showAuthor && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-foreground" data-testid={`author-name-${message.id}`}>
              {authorInfo.name}
              {authorInfo.isBot && (
                <span className="ml-1 px-1 py-0.5 text-[10px] bg-indigo-500/20 text-indigo-400 rounded">
                  BOT
                </span>
              )}
            </span>
            <span className="text-[10px] text-slate-400">
              {formatMessageTime(timestamp)}
            </span>
          </div>
        )}
        
        <div className="relative z-0">
          <div
            className={cn(
              "rounded-lg relative group/bubble",
              isReply ? "px-2 py-1.5 text-xs" : showAuthor ? "px-3 py-2 text-sm" : "px-3 py-1 text-sm",
              isOwn 
                ? "bg-[#5a7399] text-white" 
                : "bg-muted text-foreground",
              isFromToday && "bubble-today",
              isFromToday && isOwn && "bubble-today-own"
            )}
            data-testid={`message-content-${message.id}`}
          >
            {message.content && (
              <div className="whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderMessageContent(message.content, mentionUserMap)) }} />
            )}
            {attachments.length > 0 && (
              <div className="mt-1 space-y-1">
                {attachments.map((att) => {
                  const isImage = att.metadata?.isImage || att.contentType.startsWith('image/');
                  const isVideo = att.contentType.startsWith('video/');
                  const isPdf = att.contentType === 'application/pdf';
                  const isPreviewable = isImage || isVideo || isPdf;
                  
                  const handleClick = () => {
                    if (isPreviewable && onPreviewAttachment) {
                      onPreviewAttachment({
                        id: att.id,
                        filename: att.originalFilename,
                        contentType: att.contentType,
                      });
                    } else {
                      handleDownload(att.id, att.originalFilename);
                    }
                  };
                  
                  if (isImage) {
                    return (
                      <div 
                        key={att.id} 
                        className="relative group/attachment inline-block cursor-pointer"
                        onClick={handleClick}
                        title={isPreviewable ? `Click to preview` : `Download ${att.originalFilename}`}
                      >
                        <AuthenticatedImage 
                          attachmentId={att.id}
                          alt={att.originalFilename}
                          className="max-w-full max-h-48 rounded-lg object-contain"
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(att.id, att.originalFilename);
                          }}
                          className="absolute top-1 right-1 p-1.5 bg-black/60 rounded-md opacity-0 group-hover/attachment:opacity-100 transition-opacity hover:bg-black/80"
                          title="Download"
                        >
                          <Download className="h-3.5 w-3.5 text-white" />
                        </button>
                      </div>
                    );
                  }
                  const extension = getFileExtension(att.originalFilename);
                  const FileIconComponent = getFileIcon(att.contentType, extension);
                  return (
                    <div key={att.id} className="relative group/attachment inline-block">
                      <button
                        onClick={handleClick}
                        className={cn(
                          "flex items-center gap-2 px-2 py-1.5 rounded-lg hover:opacity-80 transition-opacity",
                          isOwn ? "bg-white/10" : "bg-black/10"
                        )}
                        title={isPreviewable ? `Click to preview` : `Download ${att.originalFilename}`}
                      >
                        <FileIconComponent className={cn(
                          "h-4 w-4 shrink-0",
                          isOwn ? "text-primary-foreground/80" : "text-primary"
                        )} />
                        <div className="flex flex-col items-start min-w-0">
                          <span className={cn(
                            "text-[10px] font-medium truncate max-w-[120px]",
                            isOwn ? "text-primary-foreground" : "text-foreground"
                          )}>
                            {att.originalFilename}
                          </span>
                          <span className={cn(
                            "text-[9px]",
                            isOwn ? "text-primary-foreground/60" : "text-muted-foreground"
                          )}>
                            {att.sizeBytes ? formatFileSize(att.sizeBytes) : extension}
                          </span>
                        </div>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(att.id, att.originalFilename);
                        }}
                        className="absolute top-1 right-1 p-1 bg-black/60 rounded opacity-0 group-hover/attachment:opacity-100 transition-opacity hover:bg-black/80"
                        title="Download"
                      >
                        <Download className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            
            {channelId && (
              <div 
                className={cn(
                  "absolute -top-2 z-20 opacity-0 group-hover/bubble:opacity-100 transition-opacity flex items-center gap-1",
                  isOwn ? "-left-2" : "-right-2"
                )}
              >
                <ReactionBar
                  targetType="chat_channel_message"
                  targetId={message.id}
                  channelId={channelId}
                />
                {onReply && !isReply && (
                  <button
                    onClick={() => {
                      const authorInfo = message.author
                        ? message.author.firstName || message.author.username
                        : 'Unknown';
                      onReply(message.id, authorInfo);
                    }}
                    className="h-6 px-1.5 rounded bg-slate-700/90 backdrop-blur-sm hover:bg-slate-600 border border-white/20 text-white/90 hover:text-white transition-colors"
                    title="Reply to this message"
                  >
                    <Reply className="h-3.5 w-3.5 text-white" />
                  </button>
                )}
                {isAdmin && onDelete && (
                  <button
                    onClick={() => onDelete(message.id, message.content || '')}
                    className="h-6 px-1.5 rounded bg-red-700/90 backdrop-blur-sm hover:bg-red-600 border border-white/20 text-white/90 hover:text-white transition-colors"
                    title="Delete this message"
                  >
                    <X className="h-3.5 w-3.5 text-white" />
                  </button>
                )}
              </div>
            )}
            
            {currentUserId && hasReactions && (
              <div className="absolute -bottom-3 left-2 z-10">
                <ReactionBubbles
                  reactions={reactions}
                  currentUserId={currentUserId}
                  targetType="chat_channel_message"
                  targetId={message.id}
                  channelId={channelId}
                  isOwn={isOwn}
                />
              </div>
            )}
          </div>
        </div>
        
        {message.editedAt && (
          <span className="text-[10px] text-muted-foreground italic">
            (edited)
          </span>
        )}
      </div>
    </div>
  );
}

interface MissedCallBubbleProps {
  message: ChatMessage;
  missedCall: MissedCallMetadata;
  currentUserId?: number;
  channelId?: string;
  isHighlighted: boolean;
  messageRef: React.RefObject<HTMLDivElement>;
}

function MissedCallBubble({
  message,
  missedCall,
  currentUserId,
  channelId,
  isHighlighted,
  messageRef,
}: MissedCallBubbleProps) {
  const { startCall, activeCall, pendingOutgoing } = useCall();
  const timestamp = ensureUTCDate(message.createdAt);
  const callerName = message.author
    ? (message.author.firstName && message.author.lastName
        ? `${message.author.firstName} ${message.author.lastName}`
        : message.author.username)
    : `User ${missedCall.callerId}`;

  // Only the missed callee sees the Call Back button (callers and group
  // bystanders just see the entry). Disable while another call is in flight
  // so we don't try to stack two outbound flows.
  const isCallee = currentUserId === missedCall.calleeId;
  const callBackDisabled = !!activeCall || !!pendingOutgoing || !channelId;

  const handleCallBack = () => {
    if (!channelId) return;
    void startCall({
      channelId,
      type: missedCall.modality,
      inviteeUserIds: [missedCall.callerId],
    });
  };

  const ModalityIcon = missedCall.modality === 'video' ? Video : Phone;

  return (
    <div
      ref={messageRef}
      className={cn(
        "flex justify-center py-1",
        isHighlighted && "animate-fade-highlight rounded-lg",
      )}
      data-testid={`message-missed-call-${message.id}`}
      id={`message-${message.id}`}
    >
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/60 border border-border/40 text-xs">
        <PhoneMissed className="h-3.5 w-3.5 text-red-500 shrink-0" />
        <span className="text-foreground">
          Missed call from <span className="font-medium">{callerName}</span>
        </span>
        <span className="flex items-center gap-1 text-muted-foreground">
          <ModalityIcon className="h-3 w-3" />
          <span className="capitalize">{missedCall.modality}</span>
        </span>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{formatMessageTime(timestamp)}</span>
        {isCallee && (
          <button
            onClick={handleCallBack}
            disabled={callBackDisabled}
            className={cn(
              "ml-1 flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-colors",
              callBackDisabled
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "bg-primary text-primary-foreground hover:bg-primary/90",
            )}
            title={callBackDisabled ? 'Already in a call' : `Call back ${callerName}`}
            data-testid={`button-call-back-${message.id}`}
          >
            <ModalityIcon className="h-3 w-3" />
            Call Back
          </button>
        )}
      </div>
    </div>
  );
}

export default ChatMessageBubble;
